import _ from 'lodash';
import React from 'react';

import {
  ArrowForwardIos as ArrowForwardIosIcon,
  Edit
} from '@mui/icons-material';
import {
  Menu,
  MenuItem,
  Stack,
  Typography,
  Box,
  ListItemText,
  ListItemIcon,
  styled,
  IconButton
} from '@mui/material';
import {
  ActorStageName,
  SaveInvestorCardInput
} from 'generated/graphql';

import { appEvents } from 'modules';
import * as FavoriteInvestorCard from './FavoritedInvestorCard/FavoritedInvestorCard';
import * as InvestorCard from './InvestorCard/InvestorCard';
import * as types from '../types';



const ColumnStack = styled(Stack)(({ theme }) => ({
  minWidth: 310,
  padding: theme.spacing(1)
}));

const HeaderBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between'
}));

const CardColumnContainer = styled(Stack, {
  shouldForwardProp: prop => prop !== 'isFavorite'
})<{isFavorite?: boolean}>(({ isFavorite, theme }) => ({
  border: isFavorite ? '2px solid rgba(195, 209, 216, 0.4)' : undefined,
  borderRadius: 10,
  flexGrow: 1,
  padding: isFavorite ? theme.spacing(1) : 0
}));



interface IProps {
  expandAll: boolean;
  columnStage: ActorStageName;
  stages: types.IStage[];

  cards: types.IInvestorCard[];
  campaignId: string;
  favoritedInvestors: types.IFavoritedInvestor[];

  onSubmit: (args: SaveInvestorCardInput) => void;
}

export const FundraiseCampaignStageColumn: React.FC<IProps> = props => {
  const [anchorEl2, setAnchorEl2] = React.useState<Element | null>(null);
  const [anchorEl3, setAnchorEl3] = React.useState<Element | null>(null);

  const stage = React.useMemo(() =>
    _.find(props.stages, s => s.name === props.columnStage),
  [props.columnStage, props.stages]);



  async function handleSaveInvestorCard(stageName: ActorStageName, investorId?: string) {
    try {
      closeMenu();
      props.onSubmit({
        investorId,
        stageName
      });

    } catch (error) {
      if (error instanceof Error) {
        alert(error?.message ?? 'Unknown error');
      }
    }
  }



  function handleClickMoveToStage(id: string, stageName: ActorStageName) {
    closeMenu();
    props.onSubmit({
      id,
      stageName
    });
  }



  interface IHandleClickCreateInvestorCardArgs {
    stageName: ActorStageName;
    displayName: string;
    investorId: string;
  }

  function handleClickCreateInvestorCard(campaignId: string, args: IHandleClickCreateInvestorCardArgs) {
    closeMenu();
    props.onSubmit({
      campaignId,
      investorId: args.investorId,
      stageName: args.stageName,
      displayName: args.displayName
    });
  }



  function closeMenu() {
    setAnchorEl2(null);
    setAnchorEl3(null);
  }



  return (
    <>
      <ColumnStack spacing={2}>
        <HeaderBox>
          <Typography
            fontSize={24}
            color="#1E3949">
            {stage?.displayName}
          </Typography>

          {props.columnStage !== 'favorites' && stage && (
            <IconButton
              onClick={() => {
                appEvents.broadcast({
                  type: 'modal',
                  args: {
                    type: 'editActorStageModal',
                    actorStageName: stage.name
                  }
                });
              }}>
              <Edit />
            </IconButton>
          )}
        </HeaderBox>

        <CardColumnContainer
          isFavorite={props.columnStage === 'favorites'}
          spacing={2}>
          {props.columnStage !== 'favorites' ? (
            _.map(props.cards, c => (
              <InvestorCard.FC
                toggleExpand={props.expandAll}
                card={c}
                key={c.id}
                stages={props.stages}
                onClickMoveToStage={stageName => handleClickMoveToStage(c.id, stageName)} />))

          ) : (
            _.map(props.favoritedInvestors, favoritedInvestor => (
              <FavoriteInvestorCard.FC
                key={favoritedInvestor.id}
                stages={props.stages}
                favoritedInvestor={favoritedInvestor}
                onClickCreateInvestorCard={args => handleClickCreateInvestorCard(props.campaignId, args)} />))
          )}
        </CardColumnContainer>
      </ColumnStack>

      <Menu
        anchorEl={anchorEl2}
        open={!_.isNil(anchorEl2)}
        onClose={() => {
          setAnchorEl2(null);
        }}>
        <MenuItem>
          <ListItemText onClick={evt => {
            setAnchorEl3(evt.currentTarget);
          }}>
            Move to column
          </ListItemText>

          <ListItemIcon onClick={evt => setAnchorEl3(evt.currentTarget)}>
            <ArrowForwardIosIcon fontSize="small" />
          </ListItemIcon>
        </MenuItem>
      </Menu>

      <Menu
        anchorEl={anchorEl3}
        open={!_.isNil(anchorEl3)}
        onClose={() => {
          setAnchorEl3(null);
        }}>
        {_.map(_.filter(props.stages, s => s.name !== props.columnStage), s => (
          <MenuItem
            key={s.name}>
            <ListItemText onClick={() => handleSaveInvestorCard(s.name)}>
              {s.displayName}
            </ListItemText>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};
